import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader } from '../../components/Loader';
import { SubscriptionPlanSelector } from '../../components/SubscriptionPlanSelector';
import { Checkout } from '../../components/Checkout';
import { PageContainer } from '../../components/PageContainer';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useUser } from '../../shared/use-user';
import { useRouting } from '../../shared/use-routing';
import { createSubscription } from '../../../lib/use-cases/create-subscription';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { pagePathnames } from '../page-routes';
import { MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN } from '../../../lib/shared/subscription-plans';
import styles from './PremiumPage.css';

const DELAY = 100;

function PremiumPage() {
    const { userData, isLoading: isLoadingUserData } = useUser();
    const { plans, isLoading } = useSubscriptionPlans();
    const { buildPath } = useRouting();
    const [isPending, setIsPending] = useState(false);
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    if (!isLoadingUserData && userData.isActiveSubscription) {
        return <Navigate to={buildPath(pagePathnames.dashboard)} replace />;
    }

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            logEvent(EVENT_TYPES.PREMIUM_INITIATE_CHECKOUT);

            const newMerchantData = await createSubscription({
                plan: subscriptionData.plan,
                email: userData.email,
                metadata: {
                    screenWidth: window.screen.width,
                    screenHeight: window.screen.height
                }
            });

            selectSubscription(subscriptionData);
            setMerchantData(newMerchantData);
        } catch (e) {
            alert(e.message || 'Failed create subscription'); /* eslint-disable-line */
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscription({
                plan: subscriptionData.plan,
                email: userData.email,
                id: subscriptionId,
                metadata: {
                    screenWidth: window.screen.width,
                    screenHeight: window.screen.height
                }
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            alert(e.message || 'Failed create subscription'); /* eslint-disable-line */
        } finally {
            setIsPending(false);
        }
    };

    const closeCheckout = () => {
        selectSubscription({});
        setMerchantData({});

        setTimeout(() => {
            document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, DELAY);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loaderWrapper}>
                    <Loader variant='secondary' />
                </div>
            );
        }

        return (
            <SubscriptionPlanSelector
                onSubmit={submitSubscription}
                defaultPlan={MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN}
                isPending={isPending}
                plans={plans}
                className={styles.subscriptionPlanSelector}
            />
        );
    };

    if (merchantData.merchant) {
        return (
            <div className={styles.fixedModal}>
                <Checkout
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    onClose={closeCheckout}
                    tryAgain={tryAgain}
                    isPending={isPending}
                />
            </div>
        );
    }

    return (
        <PageContainer
            title='Upgrade to Premium to Keep Every Memory'
        >
            {renderContent()}
        </PageContainer>
    );
}

export {
    PremiumPage
};
